// @file The store for handling the guided templates discussion board on surface
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException } from '@@/bits/error_tracker'
import { DiscussionStyle } from '@@/enums'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import PadletApi from '@@/surface/padlet_api'
import type {
  DebateDiscussionQuestion,
  DebateResponse,
  GuidedDiscussionQuestion,
  GuidedDiscussionQuestionsResponse,
  GuidedDiscussionResponse,
  GuidedDiscussionResponsesResponse,
} from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum GuidedSubpanel {
  Generate = 'generate',
  Configure = 'configure',
  Post = 'post',
  Share = 'share',
}

export enum GenerationStatus {
  Initial = 'Initial',
  Generating = 'Generating',
  Generated = 'Generated',
  Errored = 'Errored',
}

interface GeneratePanelSnapshot {
  discussionTopic: string
  discussionGrades: string[]
}

interface ConfigurePanelSnapshot {
  discussionPrompt: string
  discussionStyle: string
}

interface PostPanelSnapshot {
  discussionTopic: string
  discussionGrades: string[]
  discussionPrompt: string
  discussionStyle: string
}

export const useSurfaceGuidedTemplateDiscussionBoardStore = defineStore(
  'surfaceGuidedTemplateDiscussionBoardStore',
  () => {
    const surfaceSectionsStore = useSurfaceSectionsStore()
    const surfaceSettingsStore = useSurfaceSettingsStore()
    const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
    const surfaceDraftsStore = useSurfaceDraftsStore()

    // ------------------------------
    // General
    // ------------------------------

    const activePanel = ref<GuidedSubpanel | null>(GuidedSubpanel.Generate)

    function setActivePanel(panel: GuidedSubpanel): void {
      trackEvent('Onboarding discussion board', 'Changed active panel', panel)
      // Reset our retry statuses
      hasRetriedGeneratingDiscussionQuestions.value = false
      hasRetriedGeneratingDiscussionResponses.value = false
      if (panel === GuidedSubpanel.Post) {
        if (shouldGenerateDiscussionResponses.value) {
          void generateDiscussionResponses()
        }
      }
      activePanel.value = panel
    }

    const xEmbedPanel = ref(false)
    function setEmbedPanel(value: boolean): void {
      trackEvent('Onboarding discussion board', 'Opened embed panel')
      xEmbedPanel.value = value
    }

    async function handleSubmitShortcut(): Promise<void> {
      if (activePanel.value === GuidedSubpanel.Generate && isGeneratePanelFormValid.value) {
        await generateDiscussionQuestions()
      } else if (activePanel.value === GuidedSubpanel.Configure && isConfigurePanelFormValid.value) {
        setActivePanel(GuidedSubpanel.Post)
      }
    }

    function isFieldEmpty(field: string | string[]): boolean {
      if (Array.isArray(field)) {
        return field.length === 0
      } else {
        return field.trim().length === 0
      }
    }

    // ------------------------------
    // Discussion board panel state
    // ------------------------------

    const discussionTopic = ref('')
    function setDiscussionTopic(topic: string): void {
      discussionTopic.value = topic
    }

    const discussionGrades = ref<string[]>([])
    function updateDiscussionGrades(grade: string): void {
      if (discussionGrades.value.includes(grade)) {
        discussionGrades.value = discussionGrades.value.filter((item) => item !== grade)
      } else {
        discussionGrades.value = [...discussionGrades.value, grade]
      }
    }

    // The discussion prompt is the overall question that the teacher wants to ask the students, discussion questions are just options for the discussion prompt
    const discussionPrompt = ref('')
    function setDiscussionPrompt(prompt: string): void {
      discussionPrompt.value = prompt
    }
    // The selected discussion prompt is the question that the teacher has selected on the Generate panel, not the active discussion prompt that the teacher can edit
    const selectedDiscussionPrompt = ref('')
    function setSelectedDiscussionPrompt(prompt: string): void {
      selectedDiscussionPrompt.value = prompt
    }

    const discussionStyle = ref('')
    function setDiscussionStyle(type: string): void {
      trackEvent('Onboarding discussion board', 'Set discussion style', type)
      discussionStyle.value = type
    }

    // ------------------------------
    // Discussion board sections
    // ------------------------------

    // If debate is chosen, the discussion sections represent the two sides of the debate
    const discussionSectionOne = ref('')
    function setDiscussionSectionOne(section: string): void {
      discussionSectionOne.value = section
    }
    const discussionSectionTwo = ref('')
    function setDiscussionSectionTwo(section: string): void {
      discussionSectionTwo.value = section
    }

    const sectionOneResponses = computed(() =>
      discussionResponses.value?.filter((response) => {
        const responseWithSection = response as DebateResponse
        return responseWithSection.section.toLowerCase() === discussionSectionOne.value.toLocaleLowerCase()
      }),
    )
    const sectionTwoResponses = computed(() =>
      discussionResponses.value?.filter((response) => {
        const responseWithSection = response as DebateResponse
        return responseWithSection.section.toLowerCase() === discussionSectionTwo.value.toLowerCase()
      }),
    )

    async function setSectionsOnSurface(): Promise<void> {
      // Set sections on surface
      const sectionOne = await surfaceSectionsStore.createSectionWithName(discussionSectionOne.value)
      const sectionTwo = await surfaceSectionsStore.createSectionWithName(discussionSectionTwo.value)
      if (sectionOne != null) surfaceDraftsStore.updateActiveDraft({ wall_section_id: sectionOne.id })
      if (sectionTwo != null) surfaceDraftsStore.updateActiveDraft({ wall_section_id: sectionTwo.id })
    }

    const temporarySectionId = ref(-1) // When sections are enabled we must keep at least one section, so we save this to delete later after we add our generated sections
    async function deleteAllButOneSection(): Promise<void> {
      // Upon new response generation we delete all existing sections if the discussion style is debate
      for (const sectionId of surfaceSectionsStore.sectionIds) {
        // We need to keep at least one section as a requirement of having sections
        if (surfaceSectionsStore.numOfSections === 1) {
          temporarySectionId.value = sectionId
          break
        }
        await surfaceSectionsStore.deleteSection(sectionId, { showSnackbar: false })
      }
    }

    async function setSectionsFromDiscussionQuestions(): Promise<void> {
      if (discussionQuestions.value == null) {
        return
      }

      await deleteAllButOneSection()

      const debateQuestion = discussionQuestions.value.find(
        (question) => (question as DebateDiscussionQuestion).discussion_style === DiscussionStyle.Debate,
      ) as DebateDiscussionQuestion
      // upper case the debateQuestion.section_one and debateQuestion.section_two and save them

      setDiscussionSectionOne(debateQuestion.section_one.charAt(0).toUpperCase() + debateQuestion.section_one.slice(1))
      setDiscussionSectionTwo(debateQuestion.section_two.charAt(0).toUpperCase() + debateQuestion.section_two.slice(1))

      await setSectionsOnSurface()

      // Finally we delete the temporary section since we only want two sections for the debate format
      await surfaceSectionsStore.deleteSection(temporarySectionId.value, { showSnackbar: false })
    }

    async function setSectionsFromDiscussionResponses(): Promise<void> {
      if (discussionResponses.value === null) {
        return
      }
      await deleteAllButOneSection()

      // Extract the unique sections from the responses and save them
      const sections = new Set<string>() // A Set to hold unique section names
      discussionResponses.value.forEach((response) => {
        // Explicitly assert the type of response to have a 'section'
        const responseWithSection = response as DebateResponse
        sections.add(responseWithSection.section)
      })

      setDiscussionSectionOne(Array.from(sections)[0])
      setDiscussionSectionTwo(Array.from(sections)[1])

      await setSectionsOnSurface()

      // Finally we delete the temporary section since we only want two sections for the debate format
      await surfaceSectionsStore.deleteSection(temporarySectionId.value, { showSnackbar: false })
    }

    function toggleSectionsSelected(toggleValue: boolean): void {
      // Opening and closing the settings panel stops wall attribute preview
      // Force the start wall attributes preview to run again so that the sections toggle can be updated
      surfaceOnboardingPanelStore.setSelectedSections(toggleValue)
      surfaceSettingsStore.startWallAttributesPreview()
      if (toggleValue === surfaceOnboardingPanelStore.sectionsSaved) {
        return
      }
      surfaceSettingsStore.toggleGroupBySection()
    }

    // ------------------------------
    // Discussion questions
    // ------------------------------

    const discussionQuestions = ref<GuidedDiscussionQuestion[] | null>()
    const discussionQuestionsStatus = ref<GenerationStatus>(GenerationStatus.Initial)
    const isGeneratingDiscussionQuestions = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generating,
    )
    const isDiscussionQuestionsGenerated = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generated,
    )
    const isDiscussionQuestionsErrored = computed(
      () =>
        discussionQuestionsStatus.value === GenerationStatus.Errored && hasRetriedGeneratingDiscussionQuestions.value,
    )
    const hasRetriedGeneratingDiscussionQuestions = ref(false)

    async function generateDiscussionQuestions(): Promise<void> {
      if (!shouldGenerateDiscussionQuestions.value) {
        return
      }

      // Take snapshot for future comparison
      generatePanelSnapshot.value = {
        discussionTopic: discussionTopic.value,
        discussionGrades: discussionGrades.value,
      }

      try {
        discussionQuestionsStatus.value = GenerationStatus.Generating
        const response: JsonAPIResponse<GuidedDiscussionQuestionsResponse> =
          await PadletApi.GuidedTemplates.generateDiscussionQuestions(discussionTopic.value, discussionGrades.value)

        discussionQuestions.value = (
          response.data as JsonAPIResource<GuidedDiscussionQuestionsResponse>
        ).attributes.questions

        discussionQuestionsStatus.value = GenerationStatus.Generated
      } catch (e) {
        discussionQuestionsStatus.value = GenerationStatus.Errored
        captureFetchException(e, { source: 'SurfaceOnboardingGuidedPanelGenerateDiscussionQuestions' })
        // Retry discussion question generation once quietly
        if (!hasRetriedGeneratingDiscussionQuestions.value) {
          hasRetriedGeneratingDiscussionQuestions.value = true
          await generateDiscussionQuestions()
        }
      }
      trackEvent('Onboarding discussion board', 'Generated AI discussion questions')
    }

    // ------------------------------
    // Discussion responses
    // ------------------------------

    const discussionResponses = ref<GuidedDiscussionResponse[] | null>(null)
    const selectedDiscussionResponse = ref<GuidedDiscussionResponse | null>(null)
    function setSelectedDiscussionResponse(response: GuidedDiscussionResponse): void {
      selectedDiscussionResponse.value = response
    }
    const discussionResponsesStatus = ref<GenerationStatus>(GenerationStatus.Initial)
    const isGeneratingDiscussionResponses = computed(
      () => discussionResponsesStatus.value === GenerationStatus.Generating,
    )
    const isDiscussionResponsesErrored = computed(
      () =>
        discussionResponsesStatus.value === GenerationStatus.Errored && hasRetriedGeneratingDiscussionResponses.value,
    )
    const hasRetriedGeneratingDiscussionResponses = ref(false)
    const isDiscussionResponsesGenerated = computed(
      () => discussionResponsesStatus.value === GenerationStatus.Generated,
    )

    async function generateDiscussionResponses(): Promise<void> {
      if (discussionPrompt.value === '' || discussionStyle.value === '') {
        return
      }
      // Take snapshot for future comparison
      postPanelSnapshot.value = {
        discussionTopic: discussionTopic.value,
        discussionGrades: discussionGrades.value,
        discussionPrompt: discussionPrompt.value,
        discussionStyle: discussionStyle.value,
      }

      if (discussionStyle.value === DiscussionStyle.Debate && shouldGenerateSectionNames.value) {
        setDiscussionSectionOne('')
        setDiscussionSectionTwo('')
      }

      try {
        discussionResponsesStatus.value = GenerationStatus.Generating
        const response: JsonAPIResponse<GuidedDiscussionResponsesResponse> =
          await PadletApi.GuidedTemplates.generateDiscussionResponses(
            discussionPrompt.value,
            discussionStyle.value,
            discussionTopic.value,
            discussionGrades.value,
            discussionSectionOne.value,
            discussionSectionTwo.value,
          )
        discussionResponses.value = (
          response.data as JsonAPIResource<GuidedDiscussionResponsesResponse>
        ).attributes.responses

        const numResponses = discussionResponses.value.length
        trackEvent('Onboarding discussion board', 'Generated AI discussion responses', numResponses)

        if (discussionStyle.value === DiscussionStyle.Debate && shouldGenerateSectionNames.value) {
          await setSectionsFromDiscussionResponses()
        }

        // Cleanup: once responses are generated we reset all changed statuses
        discussionResponsesStatus.value = GenerationStatus.Generated
      } catch (e) {
        discussionResponsesStatus.value = GenerationStatus.Errored
        captureFetchException(e, { source: 'SurfaceOnboardingGuidedPanelGenerateDiscussionResponses' })
        // Retry discussion response generation once quietly
        if (!hasRetriedGeneratingDiscussionResponses.value) {
          hasRetriedGeneratingDiscussionResponses.value = true
          await generateDiscussionResponses()
        }
      }
    }

    // ------------------------------
    // Snapshots and form validation
    // ------------------------------

    // Every time we generate questions or responses, we save the initial state of the form so we can compare it later to prevent unnecessary API calls
    // For the configure panel, if the user selects any new options other than what is generated from the question endpoint we must regenerate the section names
    const generatePanelSnapshot = ref<GeneratePanelSnapshot>({
      discussionTopic: '',
      discussionGrades: [],
    })

    const configurePanelSnapshot = ref<ConfigurePanelSnapshot>({
      discussionPrompt: '',
      discussionStyle: '',
    })
    function setConfigurePanelSnapshot(discussionPrompt: string, discussionStyle: string): void {
      configurePanelSnapshot.value = {
        discussionPrompt,
        discussionStyle,
      }
    }

    const postPanelSnapshot = ref<PostPanelSnapshot>({
      discussionTopic: '',
      discussionGrades: [],
      discussionPrompt: '',
      discussionStyle: '',
    })

    const shouldGenerateSectionNames = computed(() => {
      return (
        configurePanelSnapshot.value.discussionPrompt.trim() !== discussionPrompt.value.trim() ||
        configurePanelSnapshot.value.discussionStyle !== discussionStyle.value
      )
    })

    // Track form validation states here so we can allow for keyboard shortcuts anywhere
    const isGeneratePanelFormValid = computed(() => {
      return discussionTopic.value != null && discussionTopic.value.length > 0 && discussionGrades.value.length > 0
    })
    const isConfigurePanelFormValid = computed(() => {
      return discussionPrompt.value.length > 0 && discussionStyle.value.length > 0
    })

    const shouldGenerateDiscussionQuestions = computed(() => {
      return (
        generatePanelSnapshot.value.discussionTopic.trim() !== discussionTopic.value.trim() ||
        (generatePanelSnapshot.value.discussionGrades !== discussionGrades.value &&
          !isFieldEmpty(discussionTopic.value) &&
          !isFieldEmpty(discussionGrades.value))
      )
    })

    const shouldGenerateDiscussionResponses = computed(() => {
      return (
        postPanelSnapshot.value.discussionTopic.trim() !== discussionTopic.value.trim() ||
        postPanelSnapshot.value.discussionGrades !== discussionGrades.value ||
        postPanelSnapshot.value.discussionPrompt.trim() !== discussionPrompt.value.trim() ||
        (postPanelSnapshot.value.discussionStyle !== discussionStyle.value &&
          !isFieldEmpty(discussionPrompt.value) &&
          !isFieldEmpty(discussionStyle.value))
      )
    })

    return {
      // ------------------------------
      // General
      // ------------------------------
      activePanel,
      setActivePanel,

      xEmbedPanel,
      setEmbedPanel,

      handleSubmitShortcut,
      // ------------------------------
      // Discussion board panel state
      // ------------------------------
      discussionTopic,
      setDiscussionTopic,

      discussionGrades,
      updateDiscussionGrades,

      discussionPrompt,
      setDiscussionPrompt,
      selectedDiscussionPrompt,
      setSelectedDiscussionPrompt,

      discussionStyle,
      setDiscussionStyle,

      // ------------------------------
      // Discussion board sections
      // ------------------------------
      discussionSectionOne,
      setDiscussionSectionOne,
      discussionSectionTwo,
      setDiscussionSectionTwo,

      sectionOneResponses,
      sectionTwoResponses,

      setSectionsOnSurface,
      deleteAllButOneSection,

      setSectionsFromDiscussionQuestions,
      setSectionsFromDiscussionResponses,
      toggleSectionsSelected,

      // ------------------------------
      // Discussion questions
      // ------------------------------
      discussionQuestions,
      discussionQuestionsStatus,
      isGeneratingDiscussionQuestions,
      isDiscussionQuestionsGenerated,
      isDiscussionQuestionsErrored,
      generateDiscussionQuestions,

      isGeneratePanelFormValid,
      isConfigurePanelFormValid,

      shouldGenerateSectionNames,

      // ------------------------------
      // Discussion responses
      // ------------------------------

      discussionResponses,
      selectedDiscussionResponse,
      setSelectedDiscussionResponse,
      discussionResponsesStatus,
      isGeneratingDiscussionResponses,
      isDiscussionResponsesGenerated,
      isDiscussionResponsesErrored,
      generateDiscussionResponses,

      // ------------------------------
      // Snapshots and form validation
      // ------------------------------
      generatePanelSnapshot,
      configurePanelSnapshot,
      setConfigurePanelSnapshot,
      shouldGenerateDiscussionQuestions,
      shouldGenerateDiscussionResponses,
    }
  },
)
